// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { styles } from "@insite/content-library/Widgets/ContactUs/AvailableTopics";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import IconMinus from "@rpsc/XDL/Icons/IconMinus";
import IconPlus from "@rpsc/XDL/Icons/IconPlus";
import React, { useMemo, useState } from "react";

export interface ShipmentTackingNumber {
    carrierName: string;
    trackingNumber: string;
    trackingUrl?: string;
}

export interface ShipmentTrackingNumbersStyles {
    wrapper?: InjectableCss;
    label?: TypographyPresentationProps;
    links?: InjectableCss;
    linksPrint?: InjectableCss;
    link?: LinkPresentationProps;
    showMore?: LinkPresentationProps;
}

export const shipmentTrackingNumbersStyles: ShipmentTrackingNumbersStyles = {};

const ShipmentTrackingNumbers = ({
    shipmentTrackingDetails,
}: {
    shipmentTrackingDetails?: ShipmentTackingNumber[];
}) => {
    const styles = useMemo(() => shipmentTrackingNumbersStyles, []);
    const [showMore, setShowMore] = useState(false);
    if (!shipmentTrackingDetails?.length) {
        return null;
    }
    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.label}>
                {translate(`Tracking Number${shipmentTrackingDetails.length > 1 ? "s" : ""}`)}:
            </Typography>
            <StyledWrapper {...styles.linksPrint}>
                {shipmentTrackingDetails.map(item => (
                    <Link {...styles.link} href={item.trackingUrl}>
                        {item.trackingNumber}
                    </Link>
                ))}
            </StyledWrapper>
            <StyledWrapper {...styles.links}>
                {shipmentTrackingDetails.slice(0, !showMore ? 1 : shipmentTrackingDetails.length).map(item =>
                    item.trackingUrl ? (
                        <Link {...styles.link} href={item.trackingUrl}>
                            {item.trackingNumber}
                        </Link>
                    ) : (
                        <Typography {...styles.link?.typographyProps}>{item.trackingNumber}</Typography>
                    ),
                )}
            </StyledWrapper>
            {shipmentTrackingDetails.length > 1 && (
                <Link
                    {...styles.showMore}
                    onClick={() => setShowMore(prev => !prev)}
                    icon={{ iconProps: { src: showMore ? IconMinus : IconPlus, size: 10 } }}
                >
                    {showMore ? translate("See Less") : translate("See All")}
                </Link>
            )}
        </StyledWrapper>
    );
};

export default ShipmentTrackingNumbers;
